import type { ResolvedBlock, ResolvedRow, SummarySpec } from '@diagon-alley/common';
import { evaluateSummary } from '@diagon-alley/common';
import { ActionIcon, Box, Collapse, Group, Text, Tooltip } from '@mantine/core';
import { CaretDown, CaretUp, Sparkle, WarningCircle } from '@phosphor-icons/react';
import { memo, useMemo, type FC, type JSX } from 'react';

import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';

import classes from './AiSummaryPanel.module.css';
import { formatCount } from '@utils/currency';

/**
 * Renders a compiled summary spec against a live API response.
 *
 * Evaluation happens HERE, in the browser, because it is pure arithmetic over JSON the page has
 * already fetched — so opening the panel costs no network call and no model. The identical
 * `evaluateSummary` runs server-side when the same summary is emailed, which is what keeps the
 * two from drifting.
 */

type Props = {
    title: string;
    spec: SummarySpec;
    /** Keyed by the spec's `source`. Usually one entry. */
    sources: Record<string, unknown>;
    collapsed: boolean;
    onToggleCollapse: () => void;
};

const toneClass: Record<string, string> = {
    good: classes.kpiCardGood,
    warn: classes.kpiCardWarn,
    critical: classes.kpiCardCritical,
};

const calloutToneClass: Record<string, string> = {
    good: classes.calloutGood,
    warn: classes.calloutWarn,
    critical: classes.calloutCritical,
};

/** A summary table is small and read-only, so DataSheet runs bare — no toolbar, no AI view. */
function summarySheet(
    headers: string[],
    rows: ResolvedRow[],
    identityHeader?: string,
): JSX.Element {
    const columns: DataSheetColumn<Record<string, string>>[] = headers.map(
        (header, index) => ({
            key: `c${index}`,
            header: index === 0 && identityHeader ? identityHeader : header,
            sticky: index === 0,
            width: index === 0 ? 200 : undefined,
            align: index === 0 ? undefined : 'right',
        }),
    );

    const data = rows.map((row, rowIndex) => {
        const record: Record<string, string> = { __key: `${rowIndex}` };
        // Column 0 is the row's identity; the cells fill the rest in order.
        record.c0 = row.key;
        row.cells.forEach((cell, cellIndex) => {
            record[`c${cellIndex + 1}`] = cell.value.formatted;
        });
        return record;
    });

    return (
        <DataSheet
            data={data}
            columns={columns}
            bare
            hideHeaderBar
            enableAiView={false}
            getRowKey={(row) => row.__key}
            maxHeight="auto"
        />
    );
}

const BlockView: FC<{ block: ResolvedBlock }> = memo(({ block }) => {
    if (block.type === 'heading') {
        return (
            <Box>
                <Text fw={600} size="sm">
                    {block.text}
                </Text>
                {block.subtext && (
                    <Text size="xs" c="dimmed">
                        {block.subtext}
                    </Text>
                )}
            </Box>
        );
    }

    if (block.type === 'callout') {
        return (
            <div
                className={`${classes.callout} ${
                    block.tone ? calloutToneClass[block.tone] : ''
                }`}
            >
                {block.text}
            </div>
        );
    }

    if (block.type === 'kpis') {
        const columns = block.columns ?? Math.min(block.items.length, 4);
        return (
            <Box>
                {block.title && <div className={classes.blockTitle}>{block.title}</div>}
                <div
                    className={classes.kpiGrid}
                    // The only inline style in the file: the column count is data, not design.
                    ref={(node) => {
                        if (node) {
                            node.style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
                        }
                    }}
                >
                    {block.items.map((item) => (
                        <div
                            key={item.label}
                            className={`${classes.kpiCard} ${
                                item.value.tone ? toneClass[item.value.tone] : ''
                            }`}
                        >
                            <div className={classes.kpiLabel}>{item.label}</div>
                            <div className={classes.kpiValue}>{item.value.formatted}</div>
                            {(item.share || item.helper) && (
                                <div className={classes.kpiHelper}>
                                    {[item.share, item.helper].filter(Boolean).join(' ')}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Box>
        );
    }

    if (block.type === 'table') {
        /*
         * A nested table's groups and their children carry DIFFERENT columns — a category has a
         * run rate and a shortfall, a SKU has a name, a group, a cover figure. Forcing both into
         * one column set would leave half the cells blank, so each group renders as a titled
         * section with its own small sheet of children beneath it.
         */
        if (block.variant === 'nested') {
            return (
                <Box>
                    {block.title && <div className={classes.blockTitle}>{block.title}</div>}
                    {block.rows.map((group) => (
                        <div key={group.key} className={classes.nestedGroup}>
                            <div className={classes.groupHeading}>
                                <span className={classes.groupName}>{group.key}</span>
                                {group.cells.map((cell) => (
                                    <span key={cell.label} className={classes.groupStat}>
                                        {cell.label}: {cell.value.formatted}
                                    </span>
                                ))}
                            </div>
                            {summarySheet(
                                (group.children?.[0]?.cells ?? []).map((c) => c.label),
                                (group.children ?? []).map((child) => ({
                                    // The first child cell becomes the row identity.
                                    key: child.cells[0]?.value.formatted ?? '',
                                    cells: child.cells.slice(1),
                                })),
                            )}
                        </div>
                    ))}
                </Box>
            );
        }

        const rows = block.totalRow ? [...block.rows, block.totalRow] : block.rows;
        return (
            <Box>
                {block.title && <div className={classes.blockTitle}>{block.title}</div>}
                {summarySheet(block.headers, rows, block.headers[0])}
            </Box>
        );
    }

    if (block.type === 'narrative') {
        // Resolved separately by the caller when a model is available; nothing to draw here.
        return null;
    }

    return null;
});
BlockView.displayName = 'SummaryBlockView';

const AiSummaryPanel: FC<Props> = ({
    title,
    spec,
    sources,
    collapsed,
    onToggleCollapse,
}) => {
    const { blocks, missingPaths, rowCount, elapsedMs } = useMemo(() => {
        const started = performance.now();
        const result = evaluateSummary(spec, sources);
        return { ...result, elapsedMs: Math.round(performance.now() - started) };
    }, [spec, sources]);

    const CollapseIcon = collapsed ? CaretDown : CaretUp;

    return (
        <Box className={classes.container}>
            <Group className={classes.header} gap={8}>
                <Sparkle size={14} weight="fill" color="rgb(1,149,255)" />
                <span className={classes.title}>{title}</span>
                <span className={classes.meta}>
                    {formatCount(rowCount)} rows · {elapsedMs}ms
                </span>
                <div className={classes.headerActions}>
                    <Tooltip label={collapsed ? 'Show summary' : 'Hide summary'}>
                        <ActionIcon variant="subtle" size="sm" onClick={onToggleCollapse}>
                            <CollapseIcon size={16} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            </Group>

            <Collapse in={!collapsed}>
                <Box className={classes.body}>
                    {missingPaths.length > 0 && (
                        <div className={classes.staleBanner}>
                            <WarningCircle size={16} weight="fill" />
                            <span>
                                This summary refers to {missingPaths.length} field
                                {missingPaths.length === 1 ? '' : 's'} the response no longer
                                carries ({missingPaths.join(', ')}). Regenerate it from the
                                description.
                            </span>
                        </div>
                    )}
                    {blocks.map((block, index) => (
                        <BlockView key={`${block.type}-${index}`} block={block} />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export default memo(AiSummaryPanel);
